/**
 * Runtime feature-flag context.
 *
 * Fetches `/api/config` once on app boot and caches the result for any
 * component that needs to gate UI behind a server-side feature flag
 * (currently: Audience Builder).
 *
 * Pattern mirrors the existing compile-time `MARKETING_ENABLED` flag —
 * but this one is dynamic so ops can flip the server flag without a
 * frontend rebuild and the nav/page reacts on next refresh.
 */
import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

// Inline the env read here (instead of importing from `@/App`) to avoid
// a circular dependency: App.js imports RuntimeConfigProvider, so we
// can't import named exports from App during module init.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUDIENCE_BUILDER_ENABLED_DEFAULT =
  String(process.env.REACT_APP_AUDIENCE_BUILDER_ENABLED || "true").toLowerCase() === "true";

const Ctx = createContext({
  audienceBuilderEnabled: AUDIENCE_BUILDER_ENABLED_DEFAULT,
  audienceBuilderAiEnabled: false,
  audienceBuilderExportEnabled: false,
  loaded: false,
});

export function RuntimeConfigProvider({ children }) {
  const [state, setState] = useState({
    audienceBuilderEnabled: AUDIENCE_BUILDER_ENABLED_DEFAULT,
    audienceBuilderAiEnabled: false,
    audienceBuilderExportEnabled: false,
    loaded: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/config`, { timeout: 5000 });
        if (cancelled) return;
        setState({
          audienceBuilderEnabled: !!data?.audience_builder_enabled,
          audienceBuilderAiEnabled: !!data?.audience_builder_ai_enabled,
          audienceBuilderExportEnabled: !!data?.audience_builder_export_enabled,
          loaded: true,
        });
      } catch {
        if (!cancelled) setState(s => ({ ...s, loaded: true }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export function useRuntimeConfig() {
  return useContext(Ctx);
}
